import {useState, useRef, useEffect, useCallback, useMemo} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {ChevronDown, ArrowRight, ChevronRight} from 'lucide-react';

import iconPending from '@/assets/icons/tabBar/iconPending.svg';
import iconPendingSelect from '@/assets/icons/tabBar/iconPending_select.svg';
import iconActive from '@/assets/icons/tabBar/iconActive.svg';
import iconActiveSelect from '@/assets/icons/tabBar/iconActive_select.svg';
import iconHistory from '@/assets/icons/tabBar/iconHistory.svg';
import iconHistorySelect from '@/assets/icons/tabBar/iconHistory_select.svg';
import iconMine from '@/assets/icons/tabBar/iconMine.svg';
import iconMineSelect from '@/assets/icons/tabBar/iconMine_select.svg';
import iconAvatar from '@/assets/icons/csHome/iconAvatar.svg';
import iconPassword from '@/assets/icons/csHome/iconPassword.svg';
import emptyStateImg from '@/assets/icons/csHome/emptyState.svg';
import profileAvatarImg from '@/assets/images/csHome/profileAvatar.png';
import defaultAiAvatar from '@/assets/icons/common/default_staff_logo.svg';
import useCsWebSocket from '@/hooks/useCsWebSocket';
import {
    updateCsStatusApi,
    getCsStaffInfoApi,
    getCsCountApi,
    getPendingListApi,
    getActiveListApi,
    getEndedListApi,
    acceptChatApi,
} from '@/api/csChat';
import type {CsServiceStatus, CsCount, PendingChat, ActiveChat, EndedChat} from '@/api/csChat';
import {uploadCsFile} from '@/api/csChat';
import {updateAvatarApi} from '@/api/csLogin';
import {compressImage} from '@/utils/imageCompress';
import {clearCsAuth, getCsUserInfo, setCsUserInfo} from '@/utils/store';
import styles from './index.module.css';

// ==================== 类型定义 ====================

/** 底部标签项 */
interface TabItem {
    key: string;
    label: string;
    icon: string;
    iconActive: string;
    badge?: number;
}

/** 我的页面菜单项 */
interface MineMenuItem {
    key: string;
    icon: string;
    label: string;
    onClick?: () => void;
}

/** 客服状态配置 */
interface StatusConfig {
    key: CsServiceStatus;
    label: string;
    color: string;
}

// ==================== 常量 ====================

/** 底部标签栏配置 */
const TAB_LIST: TabItem[] = [
    {key: 'pending', label: '待接入', icon: iconPending, iconActive: iconPendingSelect},
    {key: 'active', label: '进行中', icon: iconActive, iconActive: iconActiveSelect},
    {key: 'history', label: '历史', icon: iconHistory, iconActive: iconHistorySelect},
    {key: 'mine', label: '我的', icon: iconMine, iconActive: iconMineSelect},
];

/** 客服状态配置列表 */
const STATUS_LIST: StatusConfig[] = [
    {key: 1, label: '在线', color: '#5cda7e'},
    {key: 2, label: '忙碌', color: '#f5a623'},
    {key: 3, label: '离线', color: '#989a9a'},
];

/** 状态映射（key→配置） */
const STATUS_MAP = new Map<CsServiceStatus, StatusConfig>(
    STATUS_LIST.map((s) => [s.key, s]),
);

// ==================== 页面组件 ====================

export default function CsHome() {
    const navigate = useNavigate();
    const location = useLocation();
    const userInfo = getCsUserInfo();
    const currentSysUserId = userInfo?.userId || 0;

    // 从路由 state 读取返回时需要定位的 tab（由 CsChat / CsResetPassword 返回时携带）
    const returnTab = (location.state as { returnTab?: string } | null)?.returnTab;

    // 从 localStorage 恢复上次激活的 Tab（刷新后保持在原 Tab）
    const getInitialTab = (): string => {
        const saved = localStorage.getItem('csHome_activeTab');
        if (saved && TAB_LIST.some((t) => t.key === saved)) {
            return saved;
        }
        return returnTab || 'pending';
    };
    const [activeTab, setActiveTab] = useState(getInitialTab);

    // 切换 Tab 时持久化到 localStorage
    const handleTabChange = (key: string) => {
        setActiveTab(key);
        localStorage.setItem('csHome_activeTab', key);
    };
    const [avatarUrl, setAvatarUrl] = useState(userInfo?.avatar || defaultAiAvatar);
    const [nickname, setNickname] = useState(userInfo?.nickname || '客服');
    const [avatarLoading, setAvatarLoading] = useState(false);
    const [csStatus, setCsStatus] = useState<CsServiceStatus>(1);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [pendingList, setPendingList] = useState<PendingChat[]>([]);
    const [activeList, setActiveList] = useState<ActiveChat[]>([]);
    const [endedList, setEndedList] = useState<EndedChat[]>([]);
    const [csCount, setCsCount] = useState<CsCount>({myPending: 0, myActive: 0, myEnded: 0, unassigned: 0});
    const [acceptLoading, setAcceptLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const statusRef = useRef<HTMLDivElement>(null);

    // WebSocket连接
    const ws = useCsWebSocket({
        onAuthSuccess: () => {
            // 认证成功后拉取最新数据
            fetchAllData();
        },
        onNewPending: () => {
            // 收到新待接入推送，刷新列表和计数
            fetchPendingList();
            fetchCsCount();
        },
        onUserMsg: (data) => {
            // 收到用户消息，刷新进行中列表（更新lastMessage和unreadCount）
            fetchActiveList();
            fetchCsCount();
        },
        onCountUpdate: (data) => {
            // 数量更新推送
            setCsCount((prev) => ({
                myPending: data.myPending ?? prev.myPending,
                myActive: data.myActive ?? prev.myActive,
                myEnded: prev.myEnded,
                unassigned: data.unassigned ?? prev.unassigned,
            }));
            // 计数变化时同步刷新对应列表
            fetchPendingList();
            fetchActiveList();
        },
        onReadUpdate: () => {
            // 已读更新，刷新进行中列表（更新unreadCount）
            fetchActiveList();
        },
        onChatClosed: () => {
            // 会话关闭，刷新所有列表
            fetchAllData();
        },
        onKick: () => {
            // 被踢下线
            clearCsAuth();
            navigate('/csLogin', {replace: true});
        },
        onAuthFail: () => {
            // 认证失败
            clearCsAuth();
            navigate('/csLogin', {replace: true});
        },
    });

    /** 获取当前状态配置 */
    const currentStatus = STATUS_MAP.get(csStatus) || STATUS_LIST[0];

    // ==================== 数据拉取 ====================

    /** 拉取数量统计 */
    const fetchCsCount = useCallback(async () => {
        try {
            const res = await getCsCountApi();
            if (res) {
                setCsCount(res);
            }
        } catch {
            // 接口失败忽略
        }
    }, []);

    /** 拉取待接入列表 */
    const fetchPendingList = useCallback(async () => {
        try {
            const res = await getPendingListApi(1, 50);
            if (res?.records) {
                setPendingList(res.records);
            }
        } catch {
            // 接口失败忽略
        }
    }, []);

    /** 拉取进行中列表 */
    const fetchActiveList = useCallback(async () => {
        try {
            const res = await getActiveListApi(1, 50);
            if (res?.records) {
                setActiveList(res.records);
            }
        } catch {
            // 接口失败忽略
        }
    }, []);

    /** 拉取已结束列表 */
    const fetchEndedList = useCallback(async () => {
        try {
            const res = await getEndedListApi(1, 50);
            if (res?.records) {
                setEndedList(res.records);
            }
        } catch {
            // 接口失败忽略
        }
    }, []);

    /** 拉取客服自身状态 */
    const fetchStaffInfo = useCallback(async () => {
        try {
            const res = await getCsStaffInfoApi();
            if (res?.serviceStatus) {
                setCsStatus(res.serviceStatus);
            }
        } catch {
            // 接口失败忽略
        }
    }, []);

    /** 全量拉取 */
    const fetchAllData = useCallback(() => {
        fetchCsCount();
        fetchPendingList();
        fetchActiveList();
        fetchEndedList();
        fetchStaffInfo();
    }, [fetchCsCount, fetchPendingList, fetchActiveList, fetchEndedList, fetchStaffInfo]);

    // ==================== 初始化 ====================

    /** 路由 state 中的 returnTab 变化时同步切换 tab（处理从 CsChat/CsResetPassword 返回的场景） */
    useEffect(() => {
        if (returnTab) {
            setActiveTab(returnTab);
        }
    }, [returnTab]);

    /** 初始化加载 */
    useEffect(() => {
        const info = getCsUserInfo();
        if (info) {
            if (info.avatar) setAvatarUrl(info.avatar);
            if (info.nickname) setNickname(info.nickname);
        }
        fetchAllData();
    }, [fetchAllData]);

    /** 点击状态区域外部关闭下拉框 */
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (statusRef.current && !statusRef.current.contains(e.target as Node)) {
                setStatusDropdownOpen(false);
            }
        };
        if (statusDropdownOpen) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [statusDropdownOpen]);

    // ==================== 事件处理 ====================

    /** 切换客服状态 */
    const handleStatusChange = async (status: CsServiceStatus) => {
        setCsStatus(status);
        setStatusDropdownOpen(false);
        try {
            await updateCsStatusApi(status);
            // 状态变更后重新拉取列表（离线→在线会触发二次分配）
            fetchAllData();
        } catch {
            // 接口失败忽略
        }
    };

    /** 接入用户 */
    const handleAccept = async (humanChatId: string) => {
        if (acceptLoading) return;
        setAcceptLoading(true);
        try {
            await acceptChatApi({humanChatId});
            // 接入成功后跳转到对话页面
            const chat = pendingList.find((c) => c.humanChatId === humanChatId);
            navigate('/csChat', {
                state: {
                    humanChatId,
                    userNickname: chat?.userNickname || '',
                    userAvatar: chat?.userAvatar || '',
                },
            });
        } catch {
            // 接入失败忽略
        } finally {
            setAcceptLoading(false);
            fetchAllData();
        }
    };

    /** 打开进行中对话 */
    const handleOpenChat = (chat: ActiveChat) => {
        navigate('/csChat', {
            state: {
                humanChatId: chat.humanChatId,
                userNickname: chat.userNickname,
                userAvatar: chat.userAvatar,
                fromTab: 'active',
            },
        });
    };

    /** 查看已结束对话 */
    const handleViewEnded = (chat: EndedChat) => {
        navigate('/csChat', {
            state: {
                humanChatId: chat.humanChatId,
                userNickname: chat.userNickname,
                userAvatar: chat.userAvatar,
                chatStatus: 3, // 已结束
                fromTab: 'history',
            },
        });
    };

    /** 退出登录：先置为离线状态，再断开连接并清除认证 */
    const handleLogout = async () => {
        try {
            await updateCsStatusApi(3);
        } catch {
            // 离线状态更新失败不影响退出流程
        }
        ws.disconnect();
        clearCsAuth();
        navigate('/csLogin', {replace: true});
    };

    /** 修改头像 */
    const handleChangeAvatar = () => {
        fileInputRef.current?.click();
    };

    /** 处理头像文件选择并上传 */
    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 文件类型校验
        if (!file.type.startsWith('image/')) {
            console.warn('请选择图片文件');
            return;
        }
        // 文件大小校验（5MB）
        if (file.size > 5 * 1024 * 1024) {
            console.warn('图片大小不能超过5MB');
            return;
        }

        // 本地预览
        const previewUrl = URL.createObjectURL(file);
        setAvatarUrl(previewUrl);
        setAvatarLoading(true);

        try {
            // 1. 压缩图片（头像专用：96px 2x Retina，质量 0.8）
            const compressedFile = await compressImage(file, 96, 0.8);

            // 2. 调用文件上传接口，上传压缩后的图片到S3获取URL
            const avatarUrl = await uploadCsFile(compressedFile);
            if (!avatarUrl) {
                console.warn('图片上传失败，未获取到URL');
                setAvatarUrl(userInfo?.avatar || defaultAiAvatar);
                return;
            }

            // 3. 调用更新头像接口，将URL保存到数据库
            const res = await updateAvatarApi({avatarUrl});
            if (res?.avatarUrl) {
                setAvatarUrl(res.avatarUrl);
                // 同步更新 localStorage 中的客服用户信息
                const currentInfo = getCsUserInfo();
                if (currentInfo) {
                    const updatedInfo = {...currentInfo, avatar: res.avatarUrl};
                    setCsUserInfo(updatedInfo);
                }
            }
        } catch {
            // 上传失败，恢复原头像
            setAvatarUrl(userInfo?.avatar || defaultAiAvatar);
        } finally {
            setAvatarLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    /** 修改密码 */
    const handleChangePassword = () => {
        navigate('/csRestPassword');
    };

    // ==================== 我的页面配置 ====================

    const MINE_MENU_LIST: MineMenuItem[] = [
        {key: 'avatar', icon: iconAvatar, label: '修改头像', onClick: handleChangeAvatar},
        {key: 'password', icon: iconPassword, label: '修改密码', onClick: handleChangePassword},
    ];

    // ==================== 渲染 ====================

    /** 格式化等候时间（30s内显示"等候：刚刚"，其余显示"等候：X分钟"等） */
    const formatWaitTime = (seconds: number): string => {
        if (seconds < 30) return '等候：刚刚';
        if (seconds < 3600) return `等候：${Math.floor(seconds / 60)}分钟`;
        return `等候：${Math.floor(seconds / 3600)}小时`;
    };

    /** 格式化消息时间：30s内显示"刚刚"，其余显示相对时间 */
    const formatMsgTime = (timestamp: number | string | null | undefined): string => {
        if (timestamp == null || timestamp === '') return '刚刚';
        const ts = typeof timestamp === 'string' ? Number(timestamp) : timestamp;
        const diff = Date.now() - ts;
        if (diff < 30 * 1000) return '刚刚';
        if (diff < 3600 * 1000) return `${Math.floor(diff / (60 * 1000))}分钟前`;
        if (diff < 86400 * 1000) return `${Math.floor(diff / (3600 * 1000))}小时前`;
        const date = new Date(ts);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${month}-${day} ${hours}:${minutes}`;
    };

    /** 格式化历史会话日期（毫秒时间戳→YYYY-MM-DD） */
    const formatHistoryDate = (endTime: number): string => {
        const date = new Date(endTime);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    /** 格式化相对时间（毫秒时间戳→X分钟前/X小时前/X天前/X个月前） */
    const formatTimeAgo = (endTime: number): string => {
        const diff = Date.now() - endTime;
        if (diff < 60 * 1000) return '刚刚';
        if (diff < 60 * 60 * 1000) return `${Math.floor(diff / (60 * 1000))}分钟前`;
        if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / (60 * 60 * 1000))}小时前`;
        if (diff < 30 * 24 * 60 * 60 * 1000) return `${Math.floor(diff / (24 * 60 * 60 * 1000))}天前`;
        if (diff < 12 * 30 * 24 * 60 * 60 * 1000) return `${Math.floor(diff / (30 * 24 * 60 * 60 * 1000))}个月前`;
        return `${Math.floor(diff / (12 * 30 * 24 * 60 * 60 * 1000))}年前`;
    };

    // ==================== 列表排序 ====================

    /** 待接入列表：按等待时间降序（等待最久的在最上面） */
    const sortedPendingList = useMemo(() =>
            [...pendingList].sort((a, b) => b.waitSeconds - a.waitSeconds),
        [pendingList]
    );

    /** 进行中列表：按最新消息时间降序，无消息的排最前面 */
    const sortedActiveList = useMemo(() =>
            [...activeList].sort((a, b) => {
                const aTime = a.lastMsgTime == null ? Infinity : a.lastMsgTime;
                const bTime = b.lastMsgTime == null ? Infinity : b.lastMsgTime;
                return bTime - aTime;
            }),
        [activeList]
    );

    /** 已结束列表：按结束会话时间降序（最近结束的排最上方） */
    const sortedEndedList = useMemo(() =>
            [...endedList].sort((a, b) => b.endTime - a.endTime),
        [endedList]
    );

    /** 渲染"我的"内容 */
    const renderMineContent = () => (
        <div className={styles.mineContent}>
            <div className={styles.profileCard}>
                <img src={avatarUrl} alt="" className={styles.profileAvatar}/>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className={styles.hiddenInput}
                    onChange={handleAvatarChange}
                />
                <div className={styles.profileInfo}>
                    <span className={styles.profileName}>{nickname}</span>
                    <span className={styles.profileId}>工号: {currentSysUserId}</span>
                </div>
            </div>

            <div className={styles.menuCard}>
                <div className={styles.menuListContainer}>
                    {MINE_MENU_LIST.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={styles.menuItem}
                            onClick={item.onClick}
                        >
                            <div className={styles.menuItemLeft}>
                                <img src={item.icon} alt="" className={styles.menuIcon}/>
                                <span className={styles.menuText}>{item.label}</span>
                            </div>
                            <ChevronRight size={14} className={styles.menuArrow}/>
                        </button>
                    ))}
                </div>
            </div>

            <button type="button" className={styles.logoutBtn} onClick={handleLogout}>
                <span className={styles.logoutText}>退出登录</span>
            </button>

            <span className={styles.versionText}>客服工作台 v1.0.0</span>
        </div>
    );

    /** 渲染空状态 */
    const renderEmptyContent = () => (
        <div className={styles.emptyWrap}>
            <img src={emptyStateImg} alt="" className={styles.emptyIcon}/>
            <span className={styles.emptyText}>暂无用户</span>
        </div>
    );

    /** 渲染待接入内容 */
    const renderPendingContent = () => {
        if (sortedPendingList.length === 0) return renderEmptyContent();
        const pendingCount = sortedPendingList.length;
        return (
            <>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionTitle}>等待接入</span>
                    <span className={styles.sectionCount}>{pendingCount}</span>
                </div>
                <div className={styles.cardList}>
                    {sortedPendingList.map((chat) => (
                        <div key={chat.humanChatId} className={styles.cardItem}>
                            <div className={styles.cardRow}>
                                <img src={chat.userAvatar || profileAvatarImg} alt="" className={styles.avatar}/>
                                <div className={styles.cardContent}>
                                    <div className={styles.cardInfo}>
                                        <div className={styles.cardTopRow}>
                                            <span className={styles.userName}>{chat.userNickname}</span>
                                            <button
                                                type="button"
                                                className={styles.actionBtn}
                                                onClick={() => handleAccept(chat.humanChatId)}
                                                disabled={acceptLoading}
                                            >
                                                <span className={styles.actionText}>接入</span>
                                                <ArrowRight size={9} className={styles.actionIcon}/>
                                            </button>
                                        </div>
                                        <span className={styles.cardMessage}>{chat.lastMessage}</span>
                                    </div>
                                    <span className={styles.cardWaitTime}>{formatWaitTime(chat.waitSeconds)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    /** 渲染进行中内容 */
    const renderActiveContent = () => {
        if (sortedActiveList.length === 0) return renderEmptyContent();
        return (
            <div className={styles.activeListContainer}>
                <span className={styles.totalCount}>共{sortedActiveList.length}条</span>
                {sortedActiveList.map((chat) => (
                    <div
                        key={chat.humanChatId}
                        className={styles.activeCardItem}
                        onClick={() => handleOpenChat(chat)}
                    >
                        <div className={styles.activeAvatarWrap}>
                            <img src={chat.userAvatar || profileAvatarImg} alt="" className={styles.activeAvatarImg}/>
                            {chat.unreadCount > 0 && (
                                <div className={styles.activeMsgBadge}>
                                    <span className={styles.activeMsgBadgeText}>{chat.unreadCount}</span>
                                </div>
                            )}
                        </div>
                        <div className={styles.activeCardContent}>
                            <div className={styles.activeCardTopRow}>
                                <span className={styles.activeUserName}>{chat.userNickname}</span>
                                <span className={styles.activeTime}>{formatMsgTime(chat.lastMsgTime)}</span>
                            </div>
                            <span className={styles.activeMessage}>{chat.lastMessage}</span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    /** 渲染历史（已结束）内容 */
    const renderHistoryContent = () => {
        if (sortedEndedList.length === 0) return renderEmptyContent();
        return (
            <div className={styles.activeListContainer}>
                <span className={styles.totalCount}>共{sortedEndedList.length}条</span>
                {sortedEndedList.map((chat) => (
                    <div
                        key={chat.humanChatId}
                        className={styles.historyCardItem}
                        onClick={() => handleViewEnded(chat)}
                    >
                        <div className={styles.historyAvatarWrap}>
                            <img src={chat.userAvatar || profileAvatarImg} alt="" className={styles.historyAvatarImg}/>
                        </div>
                        <div className={styles.historyCardContent}>
                            <div className={styles.historyCardTopRow}>
                                <span className={styles.historyUserName}>{chat.userNickname}</span>
                                <span className={styles.historyDate}>{formatHistoryDate(chat.endTime)}</span>
                            </div>
                            <span className={styles.historyMessage}>{chat.lastMessage || '暂无消息'}</span>
                            <div className={styles.historyStatusRow}>
                                <span className={styles.historyStatusText}>已结束</span>
                                <span className={styles.historyDot}/>
                                <span className={styles.historyTimeAgo}>{formatTimeAgo(chat.endTime)}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // 标签栏badge动态计算
    const tabsWithBadge = TAB_LIST.map((tab) => {
        if (tab.key === 'pending') return {...tab, badge: csCount.myPending + csCount.unassigned};
        if (tab.key === 'active') return {...tab, badge: csCount.myActive};
        if (tab.key === 'history') return {...tab, badge: csCount.myEnded};
        return tab;
    });

    return (
        <div className={styles.container}>
            <div className={styles.gradientBg}/>

            <div className={styles.header}>
                <div className={styles.navBar}>
                    <span className={styles.navTitle}>客服工作台</span>
                    <div className={styles.statusContainer} ref={statusRef}>
                        <button
                            type="button"
                            className={styles.statusWrap}
                            onClick={(e) => {
                                e.stopPropagation();
                                setStatusDropdownOpen(!statusDropdownOpen);
                            }}
                        >
                            <span className={styles.statusDot} style={{background: currentStatus.color}}/>
                            <span className={styles.statusText}>{currentStatus.label}</span>
                            <ChevronDown size={16}
                                         className={`${styles.statusArrow} ${statusDropdownOpen ? styles.statusArrowUp : ''}`}/>
                        </button>
                        {statusDropdownOpen && (
                            <div className={styles.statusDropdown}>
                                {STATUS_LIST.map((status) => (
                                    <button
                                        key={status.key}
                                        type="button"
                                        className={`${styles.statusOption} ${csStatus === status.key ? styles.statusOptionActive : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleStatusChange(status.key);
                                        }}
                                    >
                                        <span className={styles.statusOptionDot} style={{background: status.color}}/>
                                        <span className={styles.statusOptionText}>{status.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.main}>
                {activeTab === 'pending' && renderPendingContent()}
                {activeTab === 'active' && renderActiveContent()}
                {activeTab === 'history' && renderHistoryContent()}
                {activeTab === 'mine' && renderMineContent()}
            </div>

            <div className={styles.footer}>
                <div className={styles.tabBar}>
                    {tabsWithBadge.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={styles.tabItem}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            <div className={`${styles.tabIcon} ${activeTab === tab.key ? styles.tabIconActive : ''}`}>
                                <img src={activeTab === tab.key ? tab.iconActive : tab.icon} alt={tab.label}/>
                            </div>
                            <span
                                className={`${styles.tabLabel} ${activeTab === tab.key ? styles.tabLabelActive : ''}`}>
                {tab.label}
              </span>
                            {tab.badge !== undefined && tab.badge > 0 && (
                                <div className={styles.tabBadge}>
                                    <span className={styles.tabBadgeText}>{tab.badge}</span>
                                </div>
                            )}
                        </button>
                    ))}
                </div>
                <div className={styles.safeArea}/>
            </div>
        </div>
    );
}
